import { Box, IconButton, Typography, styled } from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

export const MedicineWrapper = styled("button", {
  shouldForwardProp: (prop) => prop !== "isButton",
})<{ isButton: boolean }>(({ isButton, theme }) => ({
  width: "100%",
  padding: "0px",
  border: "none",
  pointerEvents: isButton ? "auto" : "none",
  cursor: isButton ? "pointer" : "default",
  background: theme.palette.background.paper,
}));

export const MedicineContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  backgroundColor: "#FFFFFF",
  padding: "8px",
});

export const Row = styled(Box)({
  display: "flex",
  width: "100%",
  height: "100%",
  alignItems: "center",
});

export const SpacedRow = styled(Row)({
  justifyContent: "space-between",
});

export const PriceRow = styled(SpacedRow)({
  width: "auto",
  justifyContent: "flex-end",
  alignSelf: "flex-end",
});

export const ArrowForwardIcon = styled(ArrowForwardIosIcon)(({ theme }) => ({
  width: "20px",
  height: "20px",
  color: theme.palette.icon.primary,
}));

export const DataContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isTreatmentCard" && prop !== "isSkeleton",
})<{ isTreatmentCard?: boolean; isSkeleton?: boolean }>(
  ({ isTreatmentCard = false, isSkeleton = false }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    gap: "4px",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    padding: "0px 8px 0px 0px",
    ...(isTreatmentCard && {
      padding: "8px 8px 8px 0px",
    }),
    ...(isSkeleton && {
      paddingLeft: "16px",
    }),
  }),
);

export const MedicineName = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
  maxWidth: "100%",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  paddingLeft: "8px",
}));

export const MedicineDescription = styled(Typography)<{ color?: string }>(
  ({ color, theme }) => ({
    fontSize: "0.875rem",
    fontWeight: 400,
    color: color ?? theme.palette.text.secondary,
    maxWidth: "100%",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    paddingLeft: "8px",
  }),
);

export const MedicinePriceContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: "8px",
});

export const MedicinePriceText = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 400,
  textAlign: "right",
  whiteSpace: "nowrap",
  color: theme.palette.text.secondary,
}));

export const MedicinePriceAmount = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  textAlign: "right",
  whiteSpace: "nowrap",
  color: theme.palette.text.primary,
  marginTop: "-2px",
  alignSelf: "flex-end",
}));

export const MedicineNoPriceInfo = styled(MedicinePriceAmount)({
  fontSize: "0.875rem",
  fontWeight: 500,
});

export const MedicineImage = styled("img")({
  width: "100%",
  maxWidth: "100px",
  maxHeight: "100px",
  objectFit: "contain",
  display: "inline-block",
  margin: "0px 8px",
  pointerEvents: "auto",
  cursor: "pointer",
});

export const TagContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  width: "100%",
  marginTop: "12px",
  marginBottom: "12px",
});

interface TagProps {
  backgroundColor: string;
  textColor: string;
}

export const Tag = styled(Box, {
  shouldForwardProp: (prop) => prop !== "backgroundColor" && prop !== "textColor",
})<TagProps>(({ backgroundColor, textColor }) => ({
  display: "flex",
  justifyContent: "center",
  width: "95%",
  background: backgroundColor,
  color: textColor,
  padding: "6px",
  borderRadius: "4px",
}));

export const TagText = styled(Typography)({
  fontSize: "0.75rem",
  fontWeight: 500,
  textWrap: "nowrap",
});

export const TagSpan = styled("span", {
  shouldForwardProp: (prop) => prop !== "textColor",
})<{ textColor: string }>(({ textColor }) => ({
  color: textColor,
  fontWeight: 600,
}));

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  pointerEvents: "auto",
  cursor: "pointer",
  padding: "0px",
  color: theme.palette.icon.secondary,
}));

export const ModalContentContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  padding: "16px 0px 12px 0px",
});

export const LastUpdateMessage = styled(Typography)({
  fontSize: "14px",
  fontWeight: 500,
});

export const ModalContent = styled(Typography)({
  fontSize: "14px",
  fontWeight: 400,
  marginTop: "24px",
});

export const ContactUsText = styled("span")({
  color: "#006BD6",
  textDecoration: "underline",
  cursor: "pointer",
});

export const ComparativeTableContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: "8px 16px",
  border: "1px solid #eaeaea",
  borderRadius: "8px",
  color: theme.palette.text.primary,
}));

export const HeaderRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
});

export const ComparativeTableRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: "62px",
  padding: "8px",
  borderBottom: "1px solid #eaeaea",
  "&:last-child": {
    borderBottom: "none",
  },
  width: "100%",
});

export const ComparativeTableDataRow = styled(Box)({
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
  overflow: "hidden",
});

export const MedicineInfoColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: "1 1 0",
  minWidth: "0",
  justifyContent: "center",
  alignItems: "flex-start",
});

export const MedicinePriceColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: "0 0 auto",
});

export const PharmacyInfo = styled("span", {
  shouldForwardProp: (prop) => prop !== "isMinPrice",
})<{ isMinPrice: boolean }>(({ isMinPrice, theme }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: isMinPrice ? theme.palette.primary.main : theme.palette.text.primary,
}));

export const NameInfo = styled("span")(({ theme }) => ({
  display: "block",
  textAlign: "left",
  width: "100%",
  fontSize: "12px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: theme.palette.text.secondary,
}));

export const ExpensivePrice = styled("span")({
  fontSize: "12px",
  textDecorationLine: "line-through",
  textAlign: "right",
});

export const Price = styled("span", {
  shouldForwardProp: (prop) => prop !== "isMinPrice",
})<{ isMinPrice: boolean }>(({ isMinPrice, theme }) => ({
  fontSize: "16px",
  fontWeight: 500,
  whiteSpace: "nowrap",
  color: isMinPrice ? theme.palette.primary.main : theme.palette.text.primary,
}));

export const PriceNotFoundContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: "8px 0px",
});

export const PriceNotFound = styled("span", {
  shouldForwardProp: (prop) => prop !== "isSeller",
})<{ isSeller?: boolean }>(({ isSeller, theme }) => ({
  fontSize: "14px",
  fontWeight: 400,
  textAlign: "left",
  whiteSpace: isSeller === true ? "normal" : "nowrap",
  color: isSeller === true ? theme.palette.text.primary : theme.palette.text.secondary,
}));

export const StyledPremiumIcon = styled(WorkspacePremiumIcon)(({ theme }) => ({
  fontSize: "32px",
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.extralight,
  borderRadius: "50%",
  padding: "4px",
  marginRight: "8px",
  marginLeft: "-12px",
}));

export const RowNumber = styled("span")(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginRight: "16px",
}));
